/**
 * Reducer for state.models.eln.
 */

import reactUpdate from 'immutability-helper';
import stringify from 'json-stable-stringify';
import _ from 'underscore';

import {ServerResolutionResult} from 'notebook/tables/string-resolution/StringResolutionConstants';
import {
  BulkResolveStringsToValuesFetchCompletedAction,
  StringResolutionActionType,
  SetCachedStringResolutions,
} from 'notebook/tables/string-resolution/StringResolutionReduxActions';
import {BenchlingAction} from 'redux-stores/Action';

export interface StringResolutionState {
  resolutionResultsByResolutionQuery: {[resolutionQuery: string]: ServerResolutionResult};
  stringResolutionLoadingState: {[resolutionQuery: string]: boolean};
}

export const STRING_RESOLUTION_INITIAL_STATE = {
  // Object from stringified RESOLUTION_QUERY_SHAPE to STRING_RESOLUTION_RESULT_SHAPE
  resolutionResultsByResolutionQuery: {},
  // Object from stringified RESOLUTION_QUERY_SHAPE to boolean representing whether it is currently
  // loading or has failed - in either case we should not attempt to refetch
  stringResolutionLoadingState: {},
};

/**
 * Generates a new StringResolutionState based off of the list of resolutionQueries passed in from
 * the Redux action.
 * @param action Action containing the new resolutionQueries to be saved into the state for a
 * BULK_RESOLVE_STRINGS_TO_VALUES_FETCH_COMPLETED action or the filtered set of resolutionQueries for a
 * SET_CACHED_STRING_RESOLUTIONS action
 * @returns The new state to be appropriately merged or set within the action handler
 */
function reduceForStringResolutions(
  action: BulkResolveStringsToValuesFetchCompletedAction | SetCachedStringResolutions
): StringResolutionState {
  const stringifiedResolutionQueries = action.resolutionQueries.map((resolutionQuery) =>
    stringify(resolutionQuery)
  );
  const resolutionResultsByResolutionQuery = _.object(
    _.zip(stringifiedResolutionQueries, action.resolutionResults)
  );
  const stringResolutionLoadingState = _.mapObject(
    resolutionResultsByResolutionQuery,
    (resolvedValue) => false
  );
  return {resolutionResultsByResolutionQuery, stringResolutionLoadingState};
}

export default function reduceStringResolution(
  state: StringResolutionState = STRING_RESOLUTION_INITIAL_STATE,
  action: BenchlingAction
): StringResolutionState {
  switch (action.type) {
    case StringResolutionActionType.BULK_RESOLVE_STRINGS_TO_VALUES_FETCH_STARTED:
      return reactUpdate(state, {
        stringResolutionLoadingState: {
          $merge: Object.fromEntries(
            action.resolutionQueries.map((resolutionQuery) => [stringify(resolutionQuery), true])
          ),
        },
      });
    case StringResolutionActionType.SET_CACHED_STRING_RESOLUTIONS: {
      const newState = reduceForStringResolutions(action);
      return reactUpdate(state, {
        resolutionResultsByResolutionQuery: {
          $set: newState.resolutionResultsByResolutionQuery,
        },
        stringResolutionLoadingState: {
          $set: newState.stringResolutionLoadingState,
        },
      });
    }
    case StringResolutionActionType.BULK_RESOLVE_STRINGS_TO_VALUES_FETCH_COMPLETED: {
      const newState = reduceForStringResolutions(action);
      return reactUpdate(state, {
        resolutionResultsByResolutionQuery: {
          $merge: newState.resolutionResultsByResolutionQuery,
        },
        stringResolutionLoadingState: {
          $merge: newState.stringResolutionLoadingState,
        },
      });
    }
    case StringResolutionActionType.BULK_RESOLVE_STRINGS_TO_VALUES_FETCH_FAILED: {
      // Store an error result for each failed query that doesn't already have a
      // result, so that downstream consumers (e.g. TypedValueResolution) see a
      // result with an error string and produce a ValueError instead of a
      // LoadingError. Without this, getOrFetch() returns null (no result stored)
      // while isKeyLoadedOrLoading() returns true (loading state blocks refetch),
      // leaving cells permanently stuck as LoadingError. This is critical for
      // exports: LoadingError cells emit a data-loading-indicator attribute that
      // blocks the export pipeline indefinitely.
      //
      // We skip queries that already have a result to avoid overwriting a
      // FETCH_COMPLETED result that arrived before this FETCH_FAILED (e.g. when
      // the Autobatcher retries a batch that partially succeeded).
      const errorResults = Object.fromEntries(
        action.resolutionQueries
          .filter((resolutionQuery) => !state.resolutionResultsByResolutionQuery[stringify(resolutionQuery)])
          .map((resolutionQuery) => [
            stringify(resolutionQuery),
            {error: 'Resolution failed', resolvedValue: undefined},
          ])
      );
      return reactUpdate(state, {
        resolutionResultsByResolutionQuery: {
          $merge: errorResults,
        },
        stringResolutionLoadingState: {
          $merge: Object.fromEntries(
            action.resolutionQueries.map((resolutionQuery) => [stringify(resolutionQuery), true])
          ),
        },
      });
    }
    case StringResolutionActionType.CLEAR_STRING_RESOLUTION_CACHE:
      return STRING_RESOLUTION_INITIAL_STATE;
    default:
      return state;
  }
}
